import React, { forwardRef, useRef, useState } from 'react';
import { DisclosureExpand, DisclosureNext } from '@sas/react-svg';
// Import CSS last
import cx from 'classnames';
import { useKeyboardTooltip } from '../../hooks/useKeyboardTooltip';
import { useSingleAndDoubleClick } from '../../hooks/useSingleAndDoubleClick';
import { isTruncated } from '../../hooks/useTruncationTooltip';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderDesignTokens, renderInlineCssVars, useDesignTokens } from '../../theme';
import { BrowserName, getBrowserName } from '../../util';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { Checkbox } from '../Checkbox';
import { ContextMenu } from '../ContextMenu';
import { CustomTooltip } from '../CustomTooltip';
import { Icon } from '../Icon';
import { useIconA11yInfo } from '../Icon/useIconA11yInfo';
import { InvisibleText } from '../InvisibleText';
import { Label } from '../Label';
import { StatusIconMap } from './__internal__/StatusIconsList';
import bundle from './NovaComponents-Tree-gui-icu.properties';
import cn from './TreeNode.css.json';
import './TreeNode.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function isCheckedTreeNode(props) {
  return props.checked !== undefined;
}
const useTextLocalizer = createUseTextLocalizer(bundle);
export const TreeNode = /*#__PURE__*/forwardRef((props, ref) => {
  const shouldRenderCheckbox = isCheckedTreeNode(props);
  const isNodeSelected = shouldRenderCheckbox ? props.checked === 'mixed' || props.checked === true : props.selected;
  const localize = useTextLocalizer();
  const enableHorizontalScroll = props.enableHorizontalScroll ?? false;
  const hideAnimation = props.hideAnimation ?? false;
  function onLabelClick(e) {
    if (!!props.node.disabled) return;
    if (props.onSelect) props.onSelect(props.node, {
      ctrlKey: e.ctrlKey,
      shiftKey: e.shiftKey,
      metaKey: e.metaKey
    });
  }
  function onArrowClick(e) {
    e.stopPropagation();
    if (props.onArrowClick) props.onArrowClick(props.node.id);
  }
  const dt = useDesignTokens();
  const nodeLabelStyle = renderInlineCssVars({
    'label-fg': props.node.disabled ? 'var(--tree-disabled-fg)' : isNodeSelected ? 'var(--tree-selected-fg)' : 'var(--tree-fg)',
    'label-text-cursor': props.node.disabled ? 'default' : 'var(--tree-node-cursor)'
  });
  const getIconA11yInfo = useIconA11yInfo();
  const clickHandlers = useSingleAndDoubleClick({
    onSingleClick(e) {
      onLabelClick(e);
    },
    onDoubleClick() {
      props.onDoubleClick?.(props.node);
    },
    persist: true
  });
  const iconTooltip = props.node.icon ? getIconA11yInfo({
    ...('icon' in props.node.icon ? {
      ...props.node.icon
    } : {
      icon: props.node.icon
    })
  }).tooltip : undefined;
  const statusIconTooltip = props.node.statusIcon ? props.node.statusIconTooltip ?? getIconA11yInfo({
    icon: StatusIconMap[props.node.statusIcon]
  }).tooltip : undefined;
  const hasChildren = props.hasChildren || Array.isArray(props.node.children) && props.node.children.length > 0;
  const [isCustomTooltipOpen, setIsCustomTooltipOpen] = useState(false);
  const nodeLabelRef = useRef(null);
  const getNodeTooltip = () => {
    let tooltip = '';
    const nodeLabel = props.node.required ? `${props.node.text}, ${localize('tree.requiredNodeLabel.label')}` : props.node.text;
    if (iconTooltip) {
      tooltip = `${iconTooltip}, ${nodeLabel}`;
    } else {
      tooltip = nodeLabel;
    }
    if (statusIconTooltip) {
      tooltip += `, ${statusIconTooltip}`;
    }
    if (!iconTooltip && !statusIconTooltip) {
      if (nodeLabelRef.current && isTruncated(nodeLabelRef.current)) {
        tooltip = nodeLabel;
      } else {
        tooltip = '';
      }
    }
    if (props.isChildNode && props.node.customTooltip) {
      if (tooltip.length > 0) {
        tooltip += `\n\n${localize('view.keyboard.tooltip.label')}`;
      } else {
        tooltip = localize('view.keyboard.tooltip.label');
      }
    }
    return tooltip;
  };
  const setKeyboardTooltipRef = useKeyboardTooltip({
    getTooltip: getNodeTooltip
  });
  const focusRef = useRef(null);
  const [disableFocusLock, setDisableFocusLock] = useState(true);
  const iconProps = props.node.icon ? {
    className: cn.nodeIcon,
    disabled: !!props.node.disabled,
    ...('icon' in props.node.icon ? {
      ...props.node.icon
    } : {
      icon: props.node.icon
    }),
    ...(isNodeSelected && dt.themeVariant === 'high-contrast' ? {
      theme: () => ({
        '--icon-default-override-fg': 'var(--tree-selected-fg)',
        '--icon-neutral-override-fg': 'var(--tree-selected-fg)',
        '--icon-information-override-fg': 'var(--tree-selected-fg)',
        '--icon-warning-override-fg': 'var(--tree-selected-fg)',
        '--icon-alert-override-fg': 'var(--tree-selected-fg)',
        '--icon-success-override-fg': 'var(--tree-selected-fg)'
      })
    } : {})
  } : undefined;
  const labelProps = {
    required: !!props.node.required,
    wrapText: enableHorizontalScroll,
    className: cx(cn.nodeLabel, enableHorizontalScroll && cn.showScroll, props.node.nodeTextClassName),
    style: nodeLabelStyle,
    disabled: !!props.node.disabled,
    icon: shouldRenderCheckbox ? iconProps : undefined
  };
  const tokens = getTreeNodeTokens(dt, {
    getSelectionMode: props.getSelectionMode,
    hasChildren,
    disabled: props.node.disabled ?? false,
    isActionable: props.node.isActionable ?? false
  });
  const treeNodeDisabled = props.node.disabled || props.disabled;
  return /*#__PURE__*/_jsx(ContextMenu, {
    onContextMenu: e => {
      if (props.node.disabled) e?.stopPropagation();else props.onContext?.(props.node);
    },
    children: /*#__PURE__*/_jsx(CustomTooltip, {
      ...props.node.customTooltip,
      focusLockProps: {
        disabled: disableFocusLock
      },
      testId: postfixTestId(props.testId, 'customTooltip'),
      isOpen: isCustomTooltipOpen,
      onOpen: () => {
        setIsCustomTooltipOpen(true);
      },
      onClose: () => {
        setIsCustomTooltipOpen(false);
        setDisableFocusLock(true);
      },
      hideArrow: false,
      children: /*#__PURE__*/_jsx("div", {
        className: cx(cn.item, isNodeSelected && cn.selected, props.node.className),
        style: tokens,
        role: "treeitem",
        ...(hasChildren && {
          'aria-expanded': props.expanded
        }),
        ...(props?.depth && {
          'aria-level': props.depth
        }),
        ...(props?.siblingCount && {
          'aria-setsize': props.siblingCount
        }),
        ...(props?.siblingPosition && {
          'aria-posinset': props.siblingPosition
        }),
        "data-testid": props.testId,
        onClick: props.disableClickDelay ? onLabelClick : clickHandlers.onClick,
        ...(!shouldRenderCheckbox ? {
          'aria-selected': isNodeSelected
        } : {
          'aria-checked': props.checked === 'mixed' ? 'mixed' : props.checked
        }),
        "aria-disabled": !!props.node.disabled,
        onKeyUp: props.onKeyUp,
        onKeyDown: e => {
          props.onKeyDown?.(e);
          if (e.shiftKey && e.key === 'F10') {
            if (props.isChildNode && props.node.customTooltip?.content) {
              if (getBrowserName() === BrowserName.Firefox) {
                setTimeout(() => {
                  setIsCustomTooltipOpen(true);
                  setDisableFocusLock(false);
                }, 10);
              } else {
                setIsCustomTooltipOpen(true);
                setDisableFocusLock(false);
              }
              e.preventDefault();
              e.stopPropagation();
            }
          }
        },
        onFocus: props.onFocus,
        tabIndex: props.tabIndex,
        onDoubleClick: e => {
          props.disableClickDelay ? props.onDoubleClick?.(props.node) : clickHandlers.onDoubleClick?.(e);
        },
        ref: el => {
          setKeyboardTooltipRef(el);
          setRef(ref, el);
          setRef(focusRef, el);
        },
        children: /*#__PURE__*/_jsxs("div", {
          className: cx(cn.itemInner, treeNodeDisabled && isNodeSelected && props.getSelectionMode?.() === 'desktop' ? cn.disabledNode : ''),
          style: {
            ...(props?.depth && {
              // Subtract 1 from the depth if it has children to allow room for the expand/collapse icon.
              // Add 0.125 to the depth if it has no children. This provides 1.125rem (the width of the expand icon + margin)
              // difference between siblings with and without children so sibling labels align vertically.
              paddingInlineStart: `${hasChildren ? props.depth - 1 : props.depth + 0.125}rem`
            })
          },
          children: [hasChildren ? /*#__PURE__*/_jsx(Icon, {
            onClick: onArrowClick,
            disabled: props.disabled || props.node.disabled,
            ariaHidden: true,
            tooltip: "",
            className: cx(cn.expandIcon, !props.disabled && !props.node.disabled ? cn.expandIconPointerCursor : undefined, !hideAnimation ? props.expanded ? cn.iconExpandAnimation : cn.iconCollapseAnimation : undefined),
            icon: props.expanded ? DisclosureExpand : DisclosureNext,
            testId: postfixTestId(props.node.id, 'expandIcon')
          }) : null, shouldRenderCheckbox && /*#__PURE__*/_jsx(Checkbox, {
            focusable: false,
            disabled: !!props.node.disabled,
            checked: props.checked,
            onPress: e => {
              (props.disableClickDelay ? onLabelClick : clickHandlers.onClick)(e);
            },
            label: props.node.text,
            labelProps: {
              ...labelProps,
              ref: nodeLabelRef
            }
          }), iconProps && !shouldRenderCheckbox ? /*#__PURE__*/_jsx(Icon, {
            ...iconProps
          }) : null, !shouldRenderCheckbox && /*#__PURE__*/_jsx(Label, {
            ...labelProps,
            ref: nodeLabelRef,
            children: props.node.text
          }), props.node.required && /*#__PURE__*/_jsx(InvisibleText, {
            srOnly: true,
            children: localize('tree.requiredNodeLabel.label')
          }), props.node.statusIcon && /*#__PURE__*/_jsx(Icon, {
            className: cn.nodeStatusIcon,
            disabled: !!props.node.disabled,
            icon: StatusIconMap[props.node.statusIcon],
            ariaLabel: props.node.statusIconAriaLabel,
            tooltip: statusIconTooltip
          })]
        })
      })
    })
  });
});
export const getTreeNodeTokens = (dt, opts) => {
  let cursor = 'pointer';
  let background = dt.tokens['color-action-background-subtle-hover'];
  if (opts?.getSelectionMode?.() === 'singleLeaf' && opts?.hasChildren || opts?.disabled || opts?.getSelectionMode?.() === 'none' && !opts?.isActionable) {
    cursor = 'default';
    background = 'none';
  }
  return {
    ...renderDesignTokens({
      'selected-focus-bg': dt.themeVariant === 'high-contrast' ? 'var(--focus-dark-bg)' : 'var(--focus-light-bg)',
      'selected-indicator-bd': dt.tokens['color-primary'],
      'selected-indicator-width': dt.tokens['size-border-width-s'],
      'hover-cursor': cursor,
      'hover-bg': background,
      'anim-tree-exp-col-icon-dur-200': dt.tokens['duration-200'],
      'anim-tf-exit': dt.tokens['ease-out'],
      'anim-tf-in': dt.tokens['ease-in'],
      'disabled-border-color': dt.tokens['color-action-background-on-off-selected-disabled'],
      'disabled-border-size': dt.tokens['size-border-width-m']
    }, 'tree-node')
  };
};
//# sourceMappingURL=TreeNode.js.map